import { Autocomplete, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllEmailTemplate } from '../../../services/react-query/email_templates';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { EmailTemplate, EmailTemplateLink, EmailTemplateStartCondition } from '../../../types/EmailTemplate';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { Nullable } from '../../../types/utils';
import PreferencePicker from '../../preferences/picker/PreferencePicker';
import { DependencyOption } from '../../tasks/StartCondition';
import EmailStartCondition from './EmailStartCondition';

export type ModalAddLinkData = (linkingData: EmailTemplateLink) => void;

interface IAddEmailTemplateModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onAdd: ModalAddLinkData;
  emailTemplateLink?: EmailTemplateLink | null;
  dependencyOptions?: DependencyOption[];
}

type ModalState = Nullable<Omit<EmailTemplateLink, 'uuid'>> & {
  depends_on_uuid?: string | null;
  start_condition: EmailTemplateStartCondition;
};

const BLANK_STATE: ModalState = {
  email_template_uuid: null,
  email_template_name: null,
  email_template_subject: null,
  event_template_email_template_uuid: null,
  offset: null,
  roles_uuids: [],
  roles_values: [],
  depends_on_uuid: null,
  start_condition: EmailTemplateStartCondition.MANUAL,
};

export default function AddEmailTemplateModal({ isOpen, onCancel, onAdd, emailTemplateLink, dependencyOptions = [] }: IAddEmailTemplateModalProps) {
  const [modalState, setModalState] = useState<ModalState>(BLANK_STATE);

  const { data: preferences, isFetching: isPrefsFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE] } },
  });

  const { data, isLoading } = useGetAllEmailTemplate({
    params: { per_page: 0 },
  });
  const templates: EmailTemplate[] = _.defaultTo(data?.data, []);

  const selectedRoleObjects = useMemo(() => _.filter(preferences, (p) => _.includes(modalState.roles_uuids, p.uuid)), [preferences, modalState.roles_uuids]);

  // now we only need to check for start_condition
  const isValid = Boolean(modalState.email_template_uuid && modalState.start_condition);

  // sync modal state when opened or editing
  useEffect(() => {
    if (emailTemplateLink) {
      const selectedPrefs = _.filter(preferences, (p) => _.includes(emailTemplateLink.roles_uuids, p.uuid));
      setModalState({
        email_template_uuid: emailTemplateLink.email_template_uuid ?? null,
        email_template_name: emailTemplateLink.email_template_name ?? null,
        email_template_subject: emailTemplateLink.email_template_subject ?? null,
        event_template_email_template_uuid: emailTemplateLink.event_template_email_template_uuid ?? null,
        offset: emailTemplateLink.offset ?? null,
        roles_uuids: selectedPrefs.map((p) => p.uuid),
        roles_values: selectedPrefs.map((p) => p.value),
        depends_on_uuid: emailTemplateLink.depends_on_uuid ?? null,
        start_condition: emailTemplateLink.start_condition ?? EmailTemplateStartCondition.MANUAL,
      });
    } else {
      setModalState(BLANK_STATE);
    }
  }, [isOpen, emailTemplateLink, preferences]);

  const handleAdd = () => {
    if (!isValid) return;

    onAdd({
      uuid: emailTemplateLink?.uuid || crypto.randomUUID(),
      ...modalState,
    } as EmailTemplateLink);
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        Please choose which template to add
      </Typography>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Autocomplete<EmailTemplate>
          loading={isLoading}
          options={templates}
          onChange={(_, template) =>
            setModalState((prev) => ({
              ...prev,
              email_template_uuid: template?.uuid ?? null,
              email_template_name: template?.name ?? null,
              email_template_subject: template?.subject ?? null,
            }))
          }
          getOptionLabel={(template) => template?.name || ''}
          value={_.find(templates, (t) => t.uuid === modalState.email_template_uuid) || null}
          renderInput={(params) => <TextField {...params} autoFocus label="Templates" />}
        />
        <EmailStartCondition emailTemplate={modalState} setEmailTemplate={setModalState} dependencyOptions={dependencyOptions} />

        <PreferencePicker
          label="Contact Roles"
          multiple
          options={preferences ?? []}
          loading={isPrefsFetching}
          value={selectedRoleObjects}
          onValueChange={(vals) => {
            const list = (vals as Preference[] | null) ?? [];
            setModalState((prev) => ({
              ...prev,
              roles_uuids: _.map(list, 'uuid'),
              roles_values: _.map(list, 'value'),
            }));
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={!isValid} onClick={handleAdd}>
          {emailTemplateLink ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
